import {useLocation} from "@gatsbyjs/reach-router";
import {isEmpty, noop, omit, omitBy} from "lodash/fp";
import {parse, match, compile} from "path-to-regexp";
import QueryString from "query-string";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  useDeepCompareCallback,
  useDeepCompareEffect
} from "./deep-compare-effect";
const DefaultWrappedLink = forwardRef(function DefaultWrappedLinkWithRef({to, ...restProps}, ref) {
  return /* @__PURE__ */ React.createElement("a", {
    ref,
    href: to,
    ...restProps
  });
});
export function useURLParams(urlPattern, {
  isEmptyValue = (value) => typeof value === "object" ? isEmpty(value) : !value,
  defaultValue = noop,
  assignValues = (newValues) => (oldValues) => ({
    ...oldValues,
    ...newValues
  }),
  parseNumbers = true,
  parseBooleans = true,
  LinkComponent: WrappedLink = DefaultWrappedLink
} = {}) {
  let location = useLocation();
  let pathnameParams = useMemo(() => {
    return parse(urlPattern).map((token) => token.name).filter(Boolean);
  }, [urlPattern]);
  let parseLocation = useMemo(() => {
    const matchUrlPattern = match(urlPattern, {decode: decodeURIComponent});
    return ({pathname, search}) => {
      let result = matchUrlPattern(pathname);
      if (!result) {
        return null;
      }
      return {
        ...result.params,
        ...QueryString.parse(search, {parseNumbers, parseBooleans})
      };
    };
  }, [parseBooleans, parseNumbers, urlPattern]);
  let [params, setParamsState] = useState(parseLocation(location));
  const setParams = useCallback((newParams) => {
    setParamsState((params2) => {
      const applyNewParams = typeof newParams === "function" ? newParams : assignValues(newParams);
      newParams = applyNewParams(params2);
      newParams = omitBy((value, key) => isEmptyValue(value, key) || value === defaultValue(key), newParams);
      return newParams;
    });
  }, [assignValues, defaultValue, isEmptyValue]);
  useEffect(() => {
    setParams(parseLocation(location));
  }, [location.pathname, location.search]);
  useDeepCompareEffect(() => {
    if (params) {
      let pathname = compile(urlPattern, {encode: encodeURIComponent})(params);
      let search = QueryString.stringify(omit(pathnameParams, params));
      window.history.replaceState(null, "", `${pathname}${search ? "?" + search : ""}`);
    }
  }, [params, pathnameParams, urlPattern]);
  const toURL = useDeepCompareCallback((newParams) => {
    const applyNewParams = typeof newParams === "function" ? newParams : assignValues(newParams);
    newParams = applyNewParams(params);
    newParams = omitBy((value, key) => isEmptyValue(value, key) || value === defaultValue(key), newParams);
    let pathname = compile(urlPattern, {encode: encodeURIComponent})(newParams);
    let search = QueryString.stringify(omit(pathnameParams, newParams));
    return `${pathname}${search ? "?" + search : ""}`;
  }, [
    assignValues,
    defaultValue,
    isEmptyValue,
    params,
    pathnameParams,
    urlPattern
  ]);
  const Link = useMemo(() => forwardRef(function LinkWithRef({to, onClick, ...restProps}, ref) {
    return /* @__PURE__ */ React.createElement(WrappedLink, {
      ref,
      to: toURL(to),
      onClick: (event) => {
        event.preventDefault();
        setParams(to);
        if (onClick)
          onClick(event);
      },
      ...restProps
    });
  }), [setParams, toURL, WrappedLink]);
  const more = {Link, toURL};
  Object.assign(setParams, more);
  const tuple = [params || {}, setParams, more];
  Object.assign(tuple, {params: params || {}, setParams}, more);
  return tuple;
}
